import api from './api';

export const mailboxService = {
  async getMailboxes() {
    const response = await api.get('/mailboxes');
    return response.data;
  },

  async getFolders(mailboxId) {
    const url = mailboxId ? `/folders?mailboxId=${mailboxId}` : '/folders';
    const response = await api.get(url);
    return response.data;
  },

  async createMailbox(mailbox) {
    const response = await api.post('/mailboxes', mailbox);
    return response.data;
  },

  async updateMailbox(mailboxId, mailbox) {
    const response = await api.put(`/mailboxes/${mailboxId}`, mailbox);
    return response.data;
  },

  async deleteMailbox(mailboxId) {
    await api.delete(`/mailboxes/${mailboxId}`);
  },

  async testConnection(mailboxId) {
    const response = await api.post(`/mailboxes/${mailboxId}/test`);
    return response.data;
  },

  // Incremental sync by default (uses per-folder UID checkpoint). Pass
  // { fullSync: true } when you actually want to wipe the checkpoint and
  // walk every folder from scratch — that path is much slower and almost
  // never what you want on a manual "Send/Receive" press.
  async syncMailbox(mailboxId, { fullSync = false } = {}) {
    const query = fullSync ? '?fullSync=true' : '';
    const response = await api.post(`/mailboxes/${mailboxId}/sync${query}`);
    return response.data;
  },

  async cancelSync(mailboxId) {
    const response = await api.post(`/mailboxes/${mailboxId}/sync/cancel`);
    return response.data;
  },

  async getGlobalSyncStatus() {
    const response = await api.get(`/mailboxes/sync/status`);
    return response.data;
  }
};

export const messageService = {
  async getMessages(params = {}) {
    const response = await api.get('/messages', { params });
    return response.data;
  },

  async getMessage(messageId) {
    const response = await api.get(`/messages/${messageId}`);
    return response.data;
  },

  async getThread(conversationId) {
    const response = await api.get(`/messages/thread/${conversationId}`);
    return response.data;
  },

  async sendMessage(message) {
    const response = await api.post('/messages', message);
    return response.data;
  },

  async updateMessage(messageId, updates) {
    const response = await api.put(`/messages/${messageId}`, updates);
    return response.data;
  },

  async deleteMessage(messageId, permanent = false) {
    await api.delete(`/messages/${messageId}?permanent=${permanent}`);
  },

  async moveMessage(messageId, folderId) {
    const response = await api.post(`/messages/${messageId}/move`, { folderId });
    return response.data;
  },

  async searchSemantic(query) {
    const response = await api.post('/messages/search', { query });
    return response.data;
  }
};

export const agentService = {
  async getSummary(messageId) {
    const response = await api.post(`/agents/summary/message/${messageId}`);
    return response.data;
  },

  async getSmartReplies(messageId) {
    const response = await api.post(`/agents/write/reply/${messageId}`);
    return response.data;
  },

  async generateDraft(prompt, replyToId = null) {
    const response = await api.post('/agents/write/draft', { prompt, replyToId });
    return response.data;
  },

  async rewrite(text, tone) {
    const response = await api.post('/agents/write/rewrite', { text, tone });
    return response.data;
  },

  async getContext(messageId) {
    const response = await api.post(`/agents/research/context/${messageId}`);
    return response.data;
  },

  // ── Feedback loop (P1.3) — these signals tune future retrieval ranking ──

  // Accept (true) / reject (false) an AI suggestion for a message.
  async recordSuggestionFeedback(messageId, accepted, { satisfaction = null, chunksUsed = null, comment = null } = {}) {
    const response = await api.post(`/agents/feedback/suggestion/${messageId}`, {
      accepted, satisfaction, chunksUsed, comment
    });
    return response.data;
  },

  // Record the resolution outcome of an email interaction.
  async recordOutcome(messageId, { wasResolved, firstContactResolution = false, userSatisfaction = null, chunksUsed = null }) {
    const response = await api.post(`/agents/feedback/outcome/${messageId}`, {
      wasResolved, firstContactResolution, userSatisfaction, chunksUsed
    });
    return response.data;
  },

  // Explicit helpful / not-helpful feedback on a single retrieved chunk.
  async recordChunkFeedback(chunkId, wasHelpful, comment = null) {
    const response = await api.post(`/agents/feedback/chunk/${chunkId}`, { wasHelpful, comment });
    return response.data;
  }
};

